import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, CircularProgress, Accordion, AccordionSummary, AccordionDetails, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, FormControlLabel, LinearProgress, Switch, Card, CardContent } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { v4 as uuidv4 } from 'uuid';
import { collection, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../../../../Config/firebase';
import { Level, Syllabus, updateAssignedSyllabus } from '../../../../../state/subscriptionTableState/subscriptionTableSlice';
import { SubscriptionDocWithId, AssignedSyllabus } from '../../../types/SubscriptionType';
import { toast } from 'react-hot-toast';
import { useAppDispatch } from '../../../../../state/state';
import {Timestamp} from 'firebase/firestore';

interface SyllabusSectionProps {
  subscription: SubscriptionDocWithId;
  providerId: string;
  liveSubId?: string;
  importForAll?: boolean;
}

const SyllabusSection: React.FC<SyllabusSectionProps> = ({ subscription, providerId, liveSubId, importForAll = false }) => {
  const dispatch = useAppDispatch();
  
  // State for managing syllabi
  const [loading, setLoading] = useState<boolean>(true);
  const [masterSyllabi, setMasterSyllabi] = useState<Syllabus[]>([]);
  const [selectedSyllabus, setSelectedSyllabus] = useState<Syllabus | null>(null);
  
  // Accordion expanded states - set to expanded by default
  const [expandedLevel, setExpandedLevel] = useState<string | false>('all');
  const [expandedSession, setExpandedSession] = useState<string | false>('all');

  // Add new state variables for importing levels
  const [importLevelsDialogOpen, setImportLevelsDialogOpen] = useState<boolean>(false);
  const [selectedMasterSyllabus, setSelectedMasterSyllabus] = useState<Syllabus | null>(null);
  const [selectedLevelsToImport, setSelectedLevelsToImport] = useState<{[key: string]: boolean}>({});
  const [levelImportLoading, setLevelImportLoading] = useState<boolean>(false);
  const [importForAllUsers, setImportForAllUsers] = useState<boolean>(false);

  // Add new state variables for marking progress
  const [markProgressDialogOpen, setMarkProgressDialogOpen] = useState<boolean>(false);
  const [selectedLevelsForProgress, setSelectedLevelsForProgress] = useState<{[key: string]: boolean}>({});
  const [selectedSessionsForProgress, setSelectedSessionsForProgress] = useState<{[key: string]: boolean}>({});
  const [selectedTopicsForProgress, setSelectedTopicsForProgress] = useState<{[key: string]: boolean}>({});
  const [selectedSubtopicsForProgress, setSelectedSubtopicsForProgress] = useState<{[key: string]: boolean}>({});
  const [progressMarkingLoading, setProgressMarkingLoading] = useState<boolean>(false);
  const [expandedLevelsForProgress, setExpandedLevelsForProgress] = useState<{[key: string]: boolean}>({});
  const [expandedSessionsForProgress, setExpandedSessionsForProgress] = useState<{[key: string]: boolean}>({});
  const [expandedTopicsForProgress, setExpandedTopicsForProgress] = useState<{[key: string]: boolean}>({});

  // Add a refresh trigger state for auto-refresh
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  // Load master syllabi from top-level collection
  useEffect(() => {
    const loadMasterSyllabi = async () => {
      try {
        setLoading(true);
        const syllabusCollection = collection(db, 'ServiceProviders', providerId, 'Syllabi');
        const querySnapshot = await getDocs(syllabusCollection);
        
        const syllabi: Syllabus[] = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data() as Syllabus;
          syllabi.push({
            ...data,
            id: doc.id
          });
        });
        
        setMasterSyllabi(syllabi);
        setLoading(false);
      } catch (error) {
        console.error('Error loading syllabi:', error);
        toast.error('Failed to load syllabi');
        setLoading(false);
      }
    };
    
    loadMasterSyllabi();
  }, [providerId]);

  // Function to merge progress from two level arrays
  const mergeLevelProgress = (liveSubLevels: Level[], subscriptionLevels: Level[]): Level[] => {
    // If no subscription levels, return liveSubLevels as is
    if (!subscriptionLevels || subscriptionLevels.length === 0) {
      return liveSubLevels;
    }

    // If no liveSubLevels, return subscriptionLevels as is
    if (!liveSubLevels || liveSubLevels.length === 0) {
      return subscriptionLevels;
    }

    // Merge progress by taking the more advanced status
    return liveSubLevels.map(liveLevel => {
      const subLevel = subscriptionLevels.find(sl => sl.id === liveLevel.id);
      if (!subLevel) return liveLevel;

      const mergedLevel = { ...liveLevel };
      
      // Merge level status (if subscription level is completed, mark as completed)
      if (subLevel.status === 'completed') {
        mergedLevel.status = 'completed';
      }

      // Merge sessions
      mergedLevel.sessions = liveLevel.sessions.map(liveSession => {
        const subSession = subLevel.sessions?.find(ss => ss.id === liveSession.id);
        if (!subSession) return liveSession;

        const mergedSession = { ...liveSession };
        if (subSession.status === 'completed') {
          mergedSession.status = 'completed';
        }

        // Merge topics
        mergedSession.topics = liveSession.topics.map(liveTopic => {
          const subTopic = subSession.topics?.find(st => st.id === liveTopic.id);
          if (!subTopic) return liveTopic;

          const mergedTopic = { ...liveTopic };
          if (subTopic.status === 'completed') {
            mergedTopic.status = 'completed';
            mergedTopic.updatedAt = subTopic.updatedAt;
          }

          // Merge subtopics
          if (mergedTopic.subtopics && subTopic.subtopics) {
            mergedTopic.subtopics = liveTopic.subtopics?.map(liveSubtopic => {
              const subSubtopic = subTopic.subtopics?.find(sst => sst.id === liveSubtopic.id);
              if (!subSubtopic) return liveSubtopic;

              const mergedSubtopic = { ...liveSubtopic };
              if (subSubtopic.status === 'completed') {
                mergedSubtopic.status = 'completed';
                mergedSubtopic.updatedAt = subSubtopic.updatedAt;
              }

              return mergedSubtopic;
            });
          }

          return mergedTopic;
        });

        return mergedSession;
      });

      return mergedLevel;
    });
  };

  // Load subscription's levels if it exists
  useEffect(() => {
    const loadCurrentSyllabus = async () => {
      // If liveSubId is provided, load from LiveSubscription document
      if (liveSubId) {
        try {
          setLoading(true);
          
          // Load levels from both LiveSubscription and Subscription documents
          const liveSubRef = doc(db, 'ServiceProviders', providerId, 'LiveSubscriptions', liveSubId);
          const liveSubDoc = await getDoc(liveSubRef);
          
          const subscriptionRef = doc(db, 'ServiceProviders', providerId, 'Subscriptions', subscription.id);
          const subscriptionDoc = await getDoc(subscriptionRef);
          
          let liveSubLevels: Level[] = [];
          let subscriptionLevels: Level[] = [];
          
          if (liveSubDoc.exists()) {
            const liveSubData = liveSubDoc.data();
            liveSubLevels = liveSubData.levels || [];
          }
          
          
          // Merge progress from both sources
          // Changed logic to not merge levels from Subscription document
          const mergedLevels = liveSubLevels;
          
          if (mergedLevels.length > 0) {
            // Create a syllabus object for UI display using merged levels
            const updatedSyllabus = {
              id: uuidv4(),
              name: `${subscription.Name} Syllabus`,
              description: `Syllabus for ${subscription.Name}`,
              levels: mergedLevels,
              createdAt: Date.now(),
              lastModified: Date.now()
            };
            
            setSelectedSyllabus(updatedSyllabus);
            console.log("Loaded and merged levels from LiveSubscription and Subscription documents");
          } else {
            // No levels data available
            setSelectedSyllabus(null);
          }
          
          setLoading(false);
        } catch (error) {
          console.error("Error loading levels:", error);
          setSelectedSyllabus(null);
          setLoading(false);
        }
      } else {
        // No liveSubId provided
        setSelectedSyllabus(null);
        setLoading(false);
      }
    };
    
    loadCurrentSyllabus();
  }, [subscription, liveSubId, providerId, refreshTrigger]);

  // Function to calculate completion percentage for a level
  const calculateLevelCompletion = (level: Level): number => {
    if (!level.sessions || level.sessions.length === 0) return 0;
    
    let totalTopics = 0;
    let completedTopics = 0;
    
    level.sessions.forEach(session => {
      if (session.topics && session.topics.length > 0) {
        session.topics.forEach(topic => {
          totalTopics++;
          if (topic.status === 'completed') {
            completedTopics++;
          }
        });
      }
    });
    
    return totalTopics === 0 ? 0 : Math.round((completedTopics / totalTopics) * 100);
  };

  // Handle accordion expansion - support "all" state for expanded by default
  const handleLevelChange = (levelId: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    if (isExpanded) {
      setExpandedLevel(levelId);
    } else {
      setExpandedLevel(false);
    }
  };

  const handleSessionChange = (sessionId: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    if (isExpanded) {
      setExpandedSession(sessionId);
    } else {
      setExpandedSession(false);
    }
  };

  // Function to check if a level should be expanded (always true for "all" state)
  const isLevelExpanded = (levelId: string) => {
    return expandedLevel === 'all' || expandedLevel === levelId;
  };

  // Function to check if a session should be expanded (always true for "all" state)
  const isSessionExpanded = (sessionId: string) => {
    return expandedSession === 'all' || expandedSession === sessionId;
  };

  // Function to select a master syllabus to import levels from
  const handleSelectMasterSyllabus = (syllabus: Syllabus) => {
    setSelectedMasterSyllabus(syllabus);
    // Initialize the selection state
    const initialSelections: {[key: string]: boolean} = {};
    syllabus.levels.forEach(level => {
      initialSelections[level.id] = false;
    });
    setSelectedLevelsToImport(initialSelections);
  };

  // Function to toggle level selection for import
  const handleToggleLevelSelection = (levelId: string) => {
    setSelectedLevelsToImport(prev => ({
      ...prev,
      [levelId]: !prev[levelId]
    }));
  };

  // Add new function to get all live subscriptions for this subscription
  const getAllLiveSubscriptions = async () => {
    try {
      const liveSubsCollection = collection(db, 'ServiceProviders', providerId, 'LiveSubscriptions');
      const querySnapshot = await getDocs(liveSubsCollection);
      
      // Create the reference to the current subscription
      const subscriptionRef = doc(db, 'ServiceProviders', providerId, 'Subscriptions', subscription.id);
      
      // Filter live subscriptions that belong to this subscription
      const filteredSubs = querySnapshot.docs
        .filter(doc => {
          const data = doc.data();
          const subscriptionPlanRef = data.subscriptionPlanRef;
          
          return subscriptionPlanRef?.path === subscriptionRef.path;
        })
        .map(doc => ({
          id: doc.id,
          studentName: doc.data().studentName || 'Unknown Student',
          ...doc.data()
        }));
      
      return filteredSubs;
    } catch (error) {
      console.error('Error fetching live subscriptions:', error);
      toast.error('Failed to fetch live subscriptions');
      return [];
    }
  };

  // Modify handleImportLevels to handle importing for all users
  const handleImportLevels = async () => {
    if (!selectedMasterSyllabus) return;
    
    setLevelImportLoading(true);
    
    try {
      // Get the selected levels from the master syllabus
      const levelsToImport = selectedMasterSyllabus.levels
        .filter(level => selectedLevelsToImport[level.id])
        .map(level => {
          // Create a deep copy of the level
          const levelCopy: Level = JSON.parse(JSON.stringify(level));
          
          // Keep the original level ID from master syllabus, but reset status
          levelCopy.status = 'not_started';
          
          // Process sessions and their nested items
          levelCopy.sessions = (levelCopy.sessions || []).map(session => {
            session.id = session?.id || uuidv4();
            session.status = 'not_started';
            
            // Process topics
            session.topics = (session.topics || []).map(topic => {
              topic.id = topic?.id || uuidv4();
              topic.status = 'not_started';
              
              // Process subtopics
              topic.subtopics = (topic.subtopics || []).map(subtopic => {
                subtopic.id = subtopic?.id || uuidv4();
                subtopic.status = 'not_started';
                return subtopic;
              });
              
              return topic;
            });
            
            return session;
          });
          
          return levelCopy;
        });

      if (levelsToImport.length === 0) {
        toast.error('Please select at least one level to import');
        return;
      }

      if (importForAllUsers) {
        const allLiveSubs = await getAllLiveSubscriptions();
        
        if (allLiveSubs.length === 0) {
          toast.error('No students found in this subscription');
          return;
        }

        // Create assignedSyllabus object to save in Subscription document
        const assignedLevelIds = levelsToImport.map(level => level.id);
        const newAssignedSyllabus: AssignedSyllabus = {
          syllabusId: selectedMasterSyllabus.id,
          assignedLevels: assignedLevelIds
        };

        // Get current subscription data to check existing assignedSyllabus
        const subscriptionRef = doc(db, 'ServiceProviders', providerId, 'Subscriptions', subscription.id);
        const subscriptionDoc = await getDoc(subscriptionRef);
        
        if (subscriptionDoc.exists()) {
          const currentData = subscriptionDoc.data();
          const existingAssignedSyllabus = currentData.assignedSyllabus || [];
          
          // Check if this syllabus is already assigned
          const existingIndex = existingAssignedSyllabus.findIndex(
            (assigned: AssignedSyllabus) => assigned.syllabusId === selectedMasterSyllabus.id
          );
          
          let updatedAssignedSyllabus: AssignedSyllabus[];
          
          if (existingIndex >= 0) {
            // Update existing syllabus assignment - merge level IDs
            const existingEntry = existingAssignedSyllabus[existingIndex];
            const mergedLevelIds = [...new Set([...existingEntry.assignedLevels, ...assignedLevelIds])];
            updatedAssignedSyllabus = [...existingAssignedSyllabus];
            updatedAssignedSyllabus[existingIndex] = {
              ...existingEntry,
              assignedLevels: mergedLevelIds
            };
            console.log('Updated existing assignedSyllabus entry with merged levels');
          } else {
            // Add new syllabus assignment
            updatedAssignedSyllabus = [...existingAssignedSyllabus, newAssignedSyllabus];
            console.log('Added new assignedSyllabus entry');
          }
          
          // Update the Subscription document with assignedSyllabus array
          await updateDoc(subscriptionRef, {
            assignedSyllabus: updatedAssignedSyllabus
          });
          console.log('Saved assignedSyllabus array to Subscription document:', updatedAssignedSyllabus);
          
          // Update Redux state to reflect the changes
          dispatch(updateAssignedSyllabus({
            serviceProviderId: providerId,
            subscriptionId: subscription.id,
            assignedSyllabus: updatedAssignedSyllabus
          }));
        }

        let successCount = 0;
        let skippedCount = 0;
        let totalLevelsImported = 0;
        
        // Import levels for each live subscription
        for (const liveSub of allLiveSubs) {
          const docRef = doc(db, 'ServiceProviders', providerId, 'LiveSubscriptions', liveSub.id);
          const liveSubDoc = await getDoc(docRef);
          
          if (!liveSubDoc.exists()) {
            console.log(`LiveSubscription document not found for ${liveSub.id}`);
            continue;
          }
          
          const currentData = liveSubDoc.data();
          const existingLevels = currentData.levels || [];
          
          // Filter out levels that already exist (by ID)
          const newLevels = levelsToImport.filter(newLevel => 
            !existingLevels.some((existingLevel: Level) => existingLevel.id === newLevel.id)
          );
          
          if (newLevels.length > 0) {
            console.log(`Importing ${newLevels.length} new levels for ${liveSub.studentName}`);
            const updatedLevels = [...existingLevels, ...newLevels];
            totalLevelsImported += newLevels.length;
            
            // Update only levels field
            await updateDoc(docRef, {
              levels: updatedLevels
            });
            successCount++;
          } else {
            console.log(`No new levels to import for ${liveSub.studentName} - all levels already exist`);
            skippedCount++;
          }
        }
        
        if (successCount > 0) {
          toast.success(
            `Successfully imported ${totalLevelsImported} levels across ${successCount} students` +
            (skippedCount > 0 ? ` (${skippedCount} students already had these levels)` : '')
          );
        } else {
          toast('No new levels were imported - all students already have these levels');
        }
      } else if (liveSubId) {
        // Original single-user import logic
        const docRef = doc(db, 'ServiceProviders', providerId, 'LiveSubscriptions', liveSubId);
        const liveSubDoc = await getDoc(docRef);
        
        if (!liveSubDoc.exists()) {
          throw new Error('LiveSubscription document not found');
        }
        
        const currentData = liveSubDoc.data();
        const existingLevels = currentData.levels || [];
        
        // Filter out any levels that already exist (by ID)
        const newLevels = levelsToImport.filter(newLevel => 
          !existingLevels.some((existingLevel: Level) => existingLevel.id === newLevel.id)
        );
        
        if (newLevels.length > 0) {
          const updatedLevels = [...existingLevels, ...newLevels];
          
          // Update only levels field
          await updateDoc(docRef, {
            levels: updatedLevels
          });
          
          // Update local state for UI display
          const updatedSyllabus = {
            id: uuidv4(),
            name: `${subscription.Name} Syllabus`,
            description: `Syllabus for ${subscription.Name}`,
            levels: updatedLevels,
            createdAt: Date.now(),
            lastModified: Date.now()
          };
          
          setSelectedSyllabus(updatedSyllabus);
          toast.success(`Successfully imported ${newLevels.length} new levels`);
        } else {
          toast('No new levels to import - all selected levels already exist');
        }
      }
      
      // After successful import, trigger a refresh
      setRefreshTrigger(prev => prev + 1);
      
      // Close the dialog and reset states
      setImportLevelsDialogOpen(false);
      setSelectedMasterSyllabus(null);
      setSelectedLevelsToImport({});
      
      // Refresh the levels data
      if (liveSubId) {
        const liveSubRef = doc(db, 'ServiceProviders', providerId, 'LiveSubscriptions', liveSubId);
        const liveSubDoc = await getDoc(liveSubRef);
        
        if (liveSubDoc.exists()) {
          const liveSubData = liveSubDoc.data();
          const updatedSyllabus = {
            id: uuidv4(),
            name: `${subscription.Name} Syllabus`,
            description: `Syllabus for ${subscription.Name}`,
            levels: liveSubData.levels || [],
            createdAt: Date.now(),
            lastModified: Date.now()
          };
          
          setSelectedSyllabus(updatedSyllabus);
        }
      }
      
    } catch (error) {
      console.error('Error importing levels:', error);
      toast.error('Failed to import levels');
    } finally {
      setLevelImportLoading(false);
    }
  };

  // Function to update a topic's status
  const handleUpdateTopicStatus = async (levelId: string, sessionId: string, topicId: string, completed: boolean) => {
    if (!liveSubId) return;
    
    try {
      const liveSubRef = doc(db, 'ServiceProviders', providerId, 'LiveSubscriptions', liveSubId);
      const liveSubDoc = await getDoc(liveSubRef);
      
      if (!liveSubDoc.exists()) {
        toast.error('Student data not found');
        return;
      }
      
      const liveSubData = liveSubDoc.data();
      const currentLevels = liveSubData.levels || [];
      
      // Find and update the specific topic
      const updatedLevels = currentLevels.map((level: Level) => {
        if (level.id === levelId) {
          const updatedLevel = { ...level };
          updatedLevel.sessions = level.sessions.map(session => {
            if (session.id === sessionId) {
              const updatedSession = { ...session };
              updatedSession.topics = session.topics.map(topic => {
                  if (topic.id === topicId) {
                  const updatedTopic = { ...topic };
                  updatedTopic.status = completed ? 'completed' : 'not_started';
                  updatedTopic.updatedAt = Timestamp.now(); // Add timestamp
                  
                  // If marking topic as complete, mark all subtopics as complete
                  if (completed && updatedTopic.subtopics) {
                    updatedTopic.subtopics = updatedTopic.subtopics.map(subtopic => ({
                      ...subtopic,
                      status: 'completed',
                      updatedAt: Timestamp.now() // Add timestamp for subtopics too
                    }));
                  }
                  
                  return updatedTopic;
                }
                return topic;
              });
              
              // Update session status based on topics
              const allTopicsComplete = updatedSession.topics.every(t => t.status === 'completed');
              updatedSession.status = allTopicsComplete ? 'completed' : 'not_started';
              
              return updatedSession;
            }
            return session;
          });
          
          // Update level status based on sessions
          const allSessionsComplete = updatedLevel.sessions.every(s => s.status === 'completed');
          updatedLevel.status = allSessionsComplete ? 'completed' : 'not_started';
          
          return updatedLevel;
        }
        return level;
      });
      
      // Update the LiveSubscription document
      await updateDoc(liveSubRef, {
        levels: updatedLevels
      });
      
      // Also update the Subscription document if it has levels
      const subscriptionRef = doc(db, 'ServiceProviders', providerId, 'Subscriptions', subscription.id);
      const subscriptionDoc = await getDoc(subscriptionRef);
      
      if (subscriptionDoc.exists()) {
        const subscriptionData = subscriptionDoc.data();
        const subscriptionLevels = subscriptionData.levels || [];
        
        // Only update if Subscription document has levels
        if (subscriptionLevels.length > 0) {
          // Apply same update logic to subscription levels
          const updatedSubscriptionLevels = subscriptionLevels.map((level: Level) => {
            if (level.id === levelId) {
              const updatedLevel = { ...level };
              updatedLevel.sessions = level.sessions.map(session => {
                if (session.id === sessionId) {
                  const updatedSession = { ...session };
                  updatedSession.topics = session.topics.map(topic => {
                    if (topic.id === topicId) {
                      const updatedTopic = { ...topic };
                      updatedTopic.status = completed ? 'completed' : 'not_started';
                      updatedTopic.updatedAt = Timestamp.now();
                      
                      if (completed && updatedTopic.subtopics) {
                        updatedTopic.subtopics = updatedTopic.subtopics.map(subtopic => ({
                          ...subtopic,
                          status: 'completed',
                          updatedAt: Timestamp.now()
                        }));
                      }
                      
                      return updatedTopic;
                    }
                    return topic;
                  });
                  
                  const allTopicsComplete = updatedSession.topics.every(t => t.status === 'completed');
                  updatedSession.status = allTopicsComplete ? 'completed' : 'not_started';
                  
                  return updatedSession;
                }
                return session;
              });
              
              const allSessionsComplete = updatedLevel.sessions.every(s => s.status === 'completed');
              updatedLevel.status = allSessionsComplete ? 'completed' : 'not_started';
              
              return updatedLevel;
            }
            return level;
          });
          
          await updateDoc(subscriptionRef, {
            levels: updatedSubscriptionLevels
          });
        }
      }
      
      // Trigger refresh to update UI
      setRefreshTrigger(prev => prev + 1);
      
      toast.success(completed ? 'Topic marked as complete' : 'Topic marked as pending');
    } catch (error) {
      console.error('Error updating topic status:', error);
      toast.error('Failed to update topic status');
    }
  };

  // Function to delete a level
  const handleDeleteLevel = async (levelId: string) => {
    if (!liveSubId) return;
    
    try {
      const liveSubRef = doc(db, 'ServiceProviders', providerId, 'LiveSubscriptions', liveSubId);
      const liveSubDoc = await getDoc(liveSubRef);
      
      if (!liveSubDoc.exists()) {
        toast.error('Student data not found');
        return;
      }
      
      const liveSubData = liveSubDoc.data();
      const currentLevels = liveSubData.levels || [];
      
      // Filter out the level to delete
      const updatedLevels = currentLevels.filter((level: Level) => level.id !== levelId);
      
      // Update the document
      await updateDoc(liveSubRef, {
        levels: updatedLevels
      });
      
      // Trigger refresh to update UI
      setRefreshTrigger(prev => prev + 1);
      
      toast.success('Level deleted successfully');
    } catch (error) {
      console.error('Error deleting level:', error);
      toast.error('Failed to delete level');
    }
  };

  // Function to update a subtopic's status
  const handleUpdateSubtopicStatus = async (levelId: string, sessionId: string, topicId: string, subtopicId: string, completed: boolean) => {
    if (!liveSubId) return;
    
    try {
      const liveSubRef = doc(db, 'ServiceProviders', providerId, 'LiveSubscriptions', liveSubId);
      const liveSubDoc = await getDoc(liveSubRef);
      
      if (!liveSubDoc.exists()) {
        toast.error('Student data not found');
        return;
      }
      
      const liveSubData = liveSubDoc.data();
      const currentLevels = liveSubData.levels || [];
      
      // Find and update the specific subtopic
      const updatedLevels = currentLevels.map((level: Level) => {
        if (level.id === levelId) {
          const updatedLevel = { ...level };
          updatedLevel.sessions = level.sessions.map(session => {
            if (session.id === sessionId) {
              const updatedSession = { ...session };
              updatedSession.topics = session.topics.map(topic => {
                if (topic.id === topicId) {
                  const updatedTopic = { ...topic };
                  
                  if (updatedTopic.subtopics) {
                    updatedTopic.subtopics = updatedTopic.subtopics.map(subtopic => {
                      if (subtopic.id === subtopicId) {
                        return { 
                          ...subtopic, 
                          status: completed ? 'completed' : 'not_started',
                          updatedAt: Timestamp.now() // Add timestamp
                        };
                      }
                      return subtopic;
                    });
                  }
                  
                  // Update topic status based on subtopics
                  if (updatedTopic.subtopics && updatedTopic.subtopics.length > 0) {
                    const allSubtopicsComplete = updatedTopic.subtopics.every(st => st.status === 'completed');
                    updatedTopic.status = allSubtopicsComplete ? 'completed' : 'not_started';
                    if (allSubtopicsComplete) {
                      updatedTopic.updatedAt = Timestamp.now(); // Add timestamp when topic is auto-completed
                    }
                  }
                  
                  return updatedTopic;
                }
                return topic;
              });
              
              // Update session status based on topics
              const allTopicsComplete = updatedSession.topics.every(t => t.status === 'completed');
              updatedSession.status = allTopicsComplete ? 'completed' : 'not_started';
              
              return updatedSession;
            }
            return session;
          });
          
          // Update level status based on sessions
          const allSessionsComplete = updatedLevel.sessions.every(s => s.status === 'completed');
          updatedLevel.status = allSessionsComplete ? 'completed' : 'not_started';
          
          return updatedLevel;
        }
        return level;
      });
      
      // Update the LiveSubscription document
      await updateDoc(liveSubRef, {
        levels: updatedLevels
      });
      
      // Also update the Subscription document if it has levels
      const subscriptionRef = doc(db, 'ServiceProviders', providerId, 'Subscriptions', subscription.id);
      const subscriptionDoc = await getDoc(subscriptionRef);
      
      if (subscriptionDoc.exists()) {
        const subscriptionData = subscriptionDoc.data();
        const subscriptionLevels = subscriptionData.levels || [];
        
        // Only update if Subscription document has levels
        if (subscriptionLevels.length > 0) {
          // Apply same update logic to subscription levels
          const updatedSubscriptionLevels = subscriptionLevels.map((level: Level) => {
            if (level.id === levelId) {
              const updatedLevel = { ...level };
              updatedLevel.sessions = level.sessions.map(session => {
                if (session.id === sessionId) {
                  const updatedSession = { ...session };
                  updatedSession.topics = session.topics.map(topic => {
                    if (topic.id === topicId) {
                      const updatedTopic = { ...topic };
                      
                      if (updatedTopic.subtopics) {
                        updatedTopic.subtopics = updatedTopic.subtopics.map(subtopic => {
                          if (subtopic.id === subtopicId) {
                            return { 
                              ...subtopic, 
                              status: completed ? 'completed' : 'not_started',
                              updatedAt: Timestamp.now()
                            };
                          }
                          return subtopic;
                        });
                      }
                      
                      // Update topic status based on subtopics
                      if (updatedTopic.subtopics && updatedTopic.subtopics.length > 0) {
                        const allSubtopicsComplete = updatedTopic.subtopics.every(st => st.status === 'completed');
                        updatedTopic.status = allSubtopicsComplete ? 'completed' : 'not_started';
                        if (allSubtopicsComplete) {
                          updatedTopic.updatedAt = Timestamp.now();
                        }
                      }
                      
                      return updatedTopic;
                    }
                    return topic;
                  });
                  
                  const allTopicsComplete = updatedSession.topics.every(t => t.status === 'completed');
                  updatedSession.status = allTopicsComplete ? 'completed' : 'not_started';
                  
                  return updatedSession;
                }
                return session;
              });
              
              const allSessionsComplete = updatedLevel.sessions.every(s => s.status === 'completed');
              updatedLevel.status = allSessionsComplete ? 'completed' : 'not_started';
              
              return updatedLevel;
            }
            return level;
          });
          
          await updateDoc(subscriptionRef, {
            levels: updatedSubscriptionLevels
          });
        }
      }
      
      // Trigger refresh to update UI
      setRefreshTrigger(prev => prev + 1);
      
      toast.success(completed ? 'Subtopic marked as complete' : 'Subtopic marked as pending');
    } catch (error) {
      console.error('Error updating subtopic status:', error);
      toast.error('Failed to update subtopic status');
    }
  };

  // Function to open the import levels dialog
  const handleOpenImportDialog = (forAll: boolean) => {
    setImportForAllUsers(forAll);
    setSelectedLevelsToImport({});
    setSelectedMasterSyllabus(null);
    setImportLevelsDialogOpen(true);
  };

  // Function to open the mark progress dialog
  const handleOpenMarkProgressDialog = async () => {
    // Reset states
    setSelectedLevelsForProgress({});
    setSelectedSessionsForProgress({});
    setSelectedTopicsForProgress({});
    setSelectedSubtopicsForProgress({});
    setExpandedLevelsForProgress({});
    setExpandedSessionsForProgress({});
    setExpandedTopicsForProgress({});
    
    // Check if Subscription document has levels and pre-mark completed items
    try {
      const subscriptionRef = doc(db, 'ServiceProviders', providerId, 'Subscriptions', subscription.id);
      const subscriptionDoc = await getDoc(subscriptionRef);
      
      if (subscriptionDoc.exists()) {
        const subscriptionData = subscriptionDoc.data();
        const existingLevels: Level[] = subscriptionData.levels || [];
        
        console.log('Loading existing progress from Subscription document:', existingLevels.length, 'levels');
        
        if (existingLevels.length > 0) {
          // Pre-mark completed items
          const preSelectedLevels: {[key: string]: boolean} = {};
          const preSelectedSessions: {[key: string]: boolean} = {};
          const preSelectedTopics: {[key: string]: boolean} = {};
          const preSelectedSubtopics: {[key: string]: boolean} = {};
          
          existingLevels.forEach(level => {
            if (level.status === 'completed') {
              preSelectedLevels[level.id] = true;
              console.log(`Pre-marking level: ${level.name}`);
            }
            
            level.sessions?.forEach(session => {
              if (session.status === 'completed') {
                preSelectedSessions[session.id] = true;
                console.log(`Pre-marking session: ${session.name}`);
              }
              
              session.topics?.forEach(topic => {
                if (topic.status === 'completed') {
                  preSelectedTopics[topic.id] = true;
                  console.log(`Pre-marking topic: ${topic.name}`);
                }
                
                topic.subtopics?.forEach(subtopic => {
                  if (subtopic.status === 'completed') {
                    preSelectedSubtopics[subtopic.id] = true;
                    console.log(`Pre-marking subtopic: ${subtopic.name}`);
                  }
                });
              });
            });
          });
          
          // Set the pre-selected items
          setSelectedLevelsForProgress(preSelectedLevels);
          setSelectedSessionsForProgress(preSelectedSessions);
          setSelectedTopicsForProgress(preSelectedTopics);
          setSelectedSubtopicsForProgress(preSelectedSubtopics);
          
          const totalPreMarked = Object.keys(preSelectedLevels).length + 
                                 Object.keys(preSelectedSessions).length + 
                                 Object.keys(preSelectedTopics).length + 
                                 Object.keys(preSelectedSubtopics).length;
          
          if (totalPreMarked > 0) {
            console.log(`✅ Pre-marked ${totalPreMarked} completed items`);
            toast.success(`Loaded ${totalPreMarked} previously marked items`);
          }
        } else {
          console.log('No existing levels in Subscription document');
        }
      } else {
        console.log('Subscription document does not exist yet');
      }
    } catch (error) {
      console.error('Error loading existing progress:', error);
      toast.error('Failed to load existing progress');
    }
    
    setMarkProgressDialogOpen(true);
  };

  // Function to toggle level selection for progress marking
  const handleToggleLevelProgressSelection = (levelId: string) => {
    setSelectedLevelsForProgress(prev => ({
      ...prev,
      [levelId]: !prev[levelId]
    }));
  };

  // Function to toggle session selection for progress marking
  const handleToggleSessionProgressSelection = (sessionId: string) => {
    setSelectedSessionsForProgress(prev => ({
      ...prev,
      [sessionId]: !prev[sessionId]
    }));
  };

  // Function to toggle topic selection for progress marking
  const handleToggleTopicProgressSelection = (topicId: string) => {
    setSelectedTopicsForProgress(prev => ({
      ...prev,
      [topicId]: !prev[topicId]
    }));
  };

  // Function to toggle subtopic selection for progress marking
  const handleToggleSubtopicProgressSelection = (subtopicId: string) => {
    setSelectedSubtopicsForProgress(prev => ({
      ...prev,
      [subtopicId]: !prev[subtopicId]
    }));
  };

  // Function to toggle level expansion in progress dialog
  const handleToggleLevelExpansion = (levelId: string) => {
    setExpandedLevelsForProgress(prev => ({
      ...prev,
      [levelId]: !prev[levelId]
    }));
  };

  // Function to toggle session expansion in progress dialog
  const handleToggleSessionExpansion = (sessionId: string) => {
    setExpandedSessionsForProgress(prev => ({
      ...prev,
      [sessionId]: !prev[sessionId]
    }));
  };

  // Function to toggle topic expansion in progress dialog
  const handleToggleTopicExpansion = (topicId: string) => {
    setExpandedTopicsForProgress(prev => ({
      ...prev,
      [topicId]: !prev[topicId]
    }));
  };

  // Function to sync levels from assignedSyllabus to Subscription document
  const syncLevelsToSubscription = async () => {
    console.log('=== syncLevelsToSubscription called ===');
    console.log('assignedSyllabus:', subscription.assignedSyllabus);
    console.log('masterSyllabi count:', masterSyllabi.length);
    
    if (!subscription.assignedSyllabus || subscription.assignedSyllabus.length === 0) {
      console.warn('No assignedSyllabus found, returning empty array');
      return [];
    }

    try {
      // Build levels array from assignedSyllabus
      const levelsToSync: Level[] = [];
      
      subscription.assignedSyllabus.forEach(assigned => {
        console.log(`Processing assignedSyllabus with syllabusId: ${assigned.syllabusId}`);
        const syllabus = masterSyllabi.find(s => s.id === assigned.syllabusId);
        if (syllabus) {
          console.log(`Found master syllabus: ${syllabus.name}, assigned levels:`, assigned.assignedLevels);
          assigned.assignedLevels.forEach(levelId => {
            const level = syllabus.levels.find(l => l.id === levelId);
            if (level) {
              console.log(`Found level: ${level.name} (${levelId})`);
              // Create a deep copy of the level with status fields
              const levelCopy: Level = JSON.parse(JSON.stringify(level));
              levelCopy.status = 'not_started';
              
              // Process sessions and their nested items
              levelCopy.sessions = (levelCopy.sessions || []).map(session => {
                session.status = 'not_started';
                
                // Process topics
                session.topics = (session.topics || []).map(topic => {
                  topic.status = 'not_started';
                  
                  // Process subtopics
                  topic.subtopics = (topic.subtopics || []).map(subtopic => {
                    subtopic.status = 'not_started';
                    return subtopic;
                  });
                  
                  return topic;
                });
                
                return session;
              });
              
              levelsToSync.push(levelCopy);
            } else {
              console.warn(`Level not found in master syllabus: ${levelId}`);
            }
          });
        } else {
          console.warn(`Master syllabus not found: ${assigned.syllabusId}`);
        }
      });

      console.log(`Built ${levelsToSync.length} levels to sync`);

      // Update Subscription document with levels
      if (levelsToSync.length > 0) {
        const subscriptionRef = doc(db, 'ServiceProviders', providerId, 'Subscriptions', subscription.id);
        console.log(`Updating Subscription document at: ${subscriptionRef.path}`);
        
        await updateDoc(subscriptionRef, {
          levels: levelsToSync
        });
        
        console.log('✅ Successfully synced levels to Subscription document');
      } else {
        console.error('❌ No levels to sync - levelsToSync is empty');
      }

      return levelsToSync;
    } catch (error) {
      console.error('❌ Error syncing levels to Subscription:', error);
      toast.error(`Sync failed: ${error.message}`);
      throw error;
    }
  };

  // Function to get all assigned levels with their sessions, topics, and subtopics
  const getAssignedSyllabusTree = () => {
    if (!subscription.assignedSyllabus || subscription.assignedSyllabus.length === 0) {
      return [];
    }

    const tree: Array<{
      levelId: string;
      levelName: string;
      syllabusId: string;
      sessions: Array<{
        sessionId: string;
        sessionName: string;
        topics: Array<{
          topicId: string;
          topicName: string;
          subtopics: Array<{
            subtopicId: string;
            subtopicName: string;
          }>;
        }>;
      }>;
    }> = [];
    
    subscription.assignedSyllabus.forEach(assigned => {
      const syllabus = masterSyllabi.find(s => s.id === assigned.syllabusId);
      if (syllabus) {
        assigned.assignedLevels.forEach(levelId => {
          const level = syllabus.levels.find(l => l.id === levelId);
          if (level) {
            const levelTree = {
              levelId: level.id,
              levelName: level.name,
              syllabusId: assigned.syllabusId,
              sessions: level.sessions?.map(session => ({
                sessionId: session.id,
                sessionName: session.name,
                topics: session.topics?.map(topic => ({
                  topicId: topic.id,
                  topicName: topic.name,
                  subtopics: topic.subtopics?.map(subtopic => ({
                    subtopicId: subtopic.id,
                    subtopicName: subtopic.name
                  })) || []
                })) || []
              })) || []
            };
            tree.push(levelTree);
          }
        });
      }
    });

    return tree;
  };

  // Function to mark progress for all students in the batch
  const handleMarkProgress = async () => {
    console.log('=== handleMarkProgress called ===');
    console.log('subscription.id:', subscription.id);
    console.log('providerId:', providerId);
    
    if (!subscription.assignedSyllabus || subscription.assignedSyllabus.length === 0) {
      console.error('No assigned syllabi found');
      toast.error('No assigned syllabi found for this subscription');
      return;
    }

    // Get all selected items from different levels
    const selectedLevels = Object.keys(selectedLevelsForProgress).filter(
      levelId => selectedLevelsForProgress[levelId]
    );
    const selectedSessions = Object.keys(selectedSessionsForProgress).filter(
      sessionId => selectedSessionsForProgress[sessionId]
    );
    const selectedTopics = Object.keys(selectedTopicsForProgress).filter(
      topicId => selectedTopicsForProgress[topicId]
    );
    const selectedSubtopics = Object.keys(selectedSubtopicsForProgress).filter(
      subtopicId => selectedSubtopicsForProgress[subtopicId]
    );

    const totalSelected = selectedLevels.length + selectedSessions.length + selectedTopics.length + selectedSubtopics.length;

    console.log('Selected items:', {
      levels: selectedLevels.length,
      sessions: selectedSessions.length,
      topics: selectedTopics.length,
      subtopics: selectedSubtopics.length,
      total: totalSelected
    });

    if (totalSelected === 0) {
      toast.error('Please select at least one item to mark as complete');
      return;
    }

    try {
      setProgressMarkingLoading(true);
      
      // Step 1: Check if Subscription document has levels field, if not sync from assignedSyllabus
      const subscriptionRef = doc(db, 'ServiceProviders', providerId, 'Subscriptions', subscription.id);
      console.log('Fetching Subscription document from:', subscriptionRef.path);
      
      const subscriptionDoc = await getDoc(subscriptionRef);
      
      let subscriptionLevels: Level[] = [];
      
      if (subscriptionDoc.exists()) {
        console.log('✅ Subscription document exists');
        const subscriptionData = subscriptionDoc.data();
        subscriptionLevels = subscriptionData.levels || [];
        console.log('Current levels in Subscription document:', subscriptionLevels.length);
        
        // If no levels exist in Subscription document, sync from assignedSyllabus
        if (subscriptionLevels.length === 0) {
          console.log('⚠️ No levels found in Subscription document, syncing from assignedSyllabus...');
          subscriptionLevels = await syncLevelsToSubscription();
          console.log('After sync, subscriptionLevels count:', subscriptionLevels.length);
          
          if (subscriptionLevels.length > 0) {
            toast.success('Synced syllabus structure to subscription');
          } else {
            console.error('❌ Sync returned empty levels array');
            toast.error('Failed to sync syllabus structure');
          }
        } else {
          console.log('✅ Found existing levels in Subscription document');
        }
      } else {
        console.error('❌ Subscription document does not exist!');
        toast.error('Subscription document not found');
        return;
      }
      
      // Get all live subscriptions for this subscription
      const liveSubs = await getAllLiveSubscriptions();
      
      if (liveSubs.length === 0) {
        toast.error('No students found in this batch');
        return;
      }

      let updatedCount = 0;
      let errorCount = 0;

      // Update each live subscription
      for (const liveSub of liveSubs) {
        try {
          const liveSubRef = doc(db, 'ServiceProviders', providerId, 'LiveSubscriptions', liveSub.id);
          
          // Get current levels from live subscription
          const liveSubDoc = await getDoc(liveSubRef);
          if (!liveSubDoc.exists()) {
            errorCount++;
            continue;
          }

          const liveSubData = liveSubDoc.data();
          const currentLevels = liveSubData.levels || [];

          // Update the selected items to completed status
          const updatedLevels = currentLevels.map((level: Level) => {
            const updatedLevel = { ...level };
            
            // Update sessions
            updatedLevel.sessions = level.sessions.map(session => {
              const updatedSession = { ...session };
              
              // Update topics
              updatedSession.topics = session.topics.map(topic => {
                const updatedTopic = { ...topic };
                
                // Update subtopics
                updatedTopic.subtopics = topic.subtopics?.map(subtopic => {
                  const updatedSubtopic = { ...subtopic };
                  
                  // Mark subtopic as complete if selected
                  if (selectedSubtopics.includes(subtopic.id)) {
                    updatedSubtopic.status = 'completed';
                    updatedSubtopic.updatedAt = Timestamp.now();
                  }
                  
                  return updatedSubtopic;
                }) || [];
                
                // Mark topic as complete if selected or if all its subtopics are complete
                if (selectedTopics.includes(topic.id)) {
                  updatedTopic.status = 'completed';
                  updatedTopic.updatedAt = Timestamp.now();
                  // Mark all subtopics as complete when topic is marked
                  updatedTopic.subtopics = updatedTopic.subtopics.map(st => ({ ...st, status: 'completed', updatedAt: Timestamp.now() }));
                } else if (selectedSubtopics.length > 0) {
                  // Check if all subtopics are complete
                  const allSubtopicsComplete = updatedTopic.subtopics.every(st => st.status === 'completed');
                  if (allSubtopicsComplete && updatedTopic.subtopics.length > 0) {
                    updatedTopic.status = 'completed';
                    updatedTopic.updatedAt = Timestamp.now();
                  }
                }
                
                return updatedTopic;
              });
              
              // Mark session as complete if selected or if all its topics are complete
              if (selectedSessions.includes(session.id)) {
                updatedSession.status = 'completed';
                // Mark all topics and subtopics as complete when session is marked
                updatedSession.topics = updatedSession.topics.map(t => ({
                  ...t,
                  status: 'completed',
                  subtopics: t.subtopics?.map(st => ({ ...st, status: 'completed', updatedAt: Timestamp.now() })) || []
                }));
              } else if (selectedTopics.length > 0 || selectedSubtopics.length > 0) {
                // Check if all topics are complete
                const allTopicsComplete = updatedSession.topics.every(t => t.status === 'completed');
                if (allTopicsComplete && updatedSession.topics.length > 0) {
                  updatedSession.status = 'completed';
                }
              }
              
              return updatedSession;
            });
            
            // Mark level as complete if selected or if all its sessions are complete
            if (selectedLevels.includes(level.id)) {
              updatedLevel.status = 'completed';
              // Mark all sessions, topics, and subtopics as complete when level is marked
              updatedLevel.sessions = updatedLevel.sessions.map(s => ({
                ...s,
                status: 'completed',
                topics: s.topics.map(t => ({
                  ...t,
                  status: 'completed',
                  subtopics: t.subtopics?.map(st => ({ ...st, status: 'completed', updatedAt: Timestamp.now() })) || []
                }))
              }));
            } else if (selectedSessions.length > 0 || selectedTopics.length > 0 || selectedSubtopics.length > 0) {
              // Check if all sessions are complete
              const allSessionsComplete = updatedLevel.sessions.every(s => s.status === 'completed');
              if (allSessionsComplete && updatedLevel.sessions.length > 0) {
                updatedLevel.status = 'completed';
              }
            }
            
            return updatedLevel;
          });

          // Update the live subscription document
          await updateDoc(liveSubRef, {
            levels: updatedLevels
          });

          updatedCount++;
        } catch (error) {
          console.error(`Error updating live subscription ${liveSub.id}:`, error);
          errorCount++;
        }
      }

      // Step 2: Update the Subscription document's levels field with the marked progress
      console.log('Updating Subscription document with progress. Current subscriptionLevels:', subscriptionLevels.length);
      
      if (subscriptionLevels.length === 0) {
        console.error('ERROR: subscriptionLevels is empty, cannot update Subscription document');
        toast.error('Failed to sync levels - please try again');
        return;
      }

      const updatedSubscriptionLevels = subscriptionLevels.map((level: Level) => {
        const updatedLevel = { ...level };
        
        // Update sessions
        updatedLevel.sessions = level.sessions.map(session => {
          const updatedSession = { ...session };
          
          // Update topics
          updatedSession.topics = session.topics.map(topic => {
            const updatedTopic = { ...topic };
            
            // Update subtopics
            updatedTopic.subtopics = topic.subtopics?.map(subtopic => {
              const updatedSubtopic = { ...subtopic };
              
              // Mark subtopic as complete if selected
              if (selectedSubtopics.includes(subtopic.id)) {
                updatedSubtopic.status = 'completed';
                updatedSubtopic.updatedAt = Timestamp.now();
              }
              
              return updatedSubtopic;
            }) || [];
            
            // Mark topic as complete if selected or if all its subtopics are complete
            if (selectedTopics.includes(topic.id)) {
              updatedTopic.status = 'completed';
              updatedTopic.updatedAt = Timestamp.now();
              // Mark all subtopics as complete when topic is marked
              updatedTopic.subtopics = updatedTopic.subtopics.map(st => ({ ...st, status: 'completed', updatedAt: Timestamp.now() }));
            } else if (selectedSubtopics.length > 0) {
              // Check if all subtopics are complete
              const allSubtopicsComplete = updatedTopic.subtopics.every(st => st.status === 'completed');
              if (allSubtopicsComplete && updatedTopic.subtopics.length > 0) {
                updatedTopic.status = 'completed';
                updatedTopic.updatedAt = Timestamp.now();
              }
            }
            
            return updatedTopic;
          });
          
          // Mark session as complete if selected or if all its topics are complete
          if (selectedSessions.includes(session.id)) {
            updatedSession.status = 'completed';
            // Mark all topics and subtopics as complete when session is marked
            updatedSession.topics = updatedSession.topics.map(t => ({
              ...t,
              status: 'completed',
              subtopics: t.subtopics?.map(st => ({ ...st, status: 'completed', updatedAt: Timestamp.now() })) || []
            }));
          } else if (selectedTopics.length > 0 || selectedSubtopics.length > 0) {
            // Check if all topics are complete
            const allTopicsComplete = updatedSession.topics.every(t => t.status === 'completed');
            if (allTopicsComplete && updatedSession.topics.length > 0) {
              updatedSession.status = 'completed';
            }
          }
          
          return updatedSession;
        });
        
        // Mark level as complete if selected or if all its sessions are complete
        if (selectedLevels.includes(level.id)) {
          updatedLevel.status = 'completed';
          // Mark all sessions, topics, and subtopics as complete when level is marked
          updatedLevel.sessions = updatedLevel.sessions.map(s => ({
            ...s,
            status: 'completed',
            topics: s.topics.map(t => ({
              ...t,
              status: 'completed',
              subtopics: t.subtopics?.map(st => ({ ...st, status: 'completed', updatedAt: Timestamp.now() })) || []
            }))
          }));
        } else if (selectedSessions.length > 0 || selectedTopics.length > 0 || selectedSubtopics.length > 0) {
          // Check if all sessions are complete
          const allSessionsComplete = updatedLevel.sessions.every(s => s.status === 'completed');
          if (allSessionsComplete && updatedLevel.sessions.length > 0) {
            updatedLevel.status = 'completed';
          }
        }
        
        return updatedLevel;
      });

      // Update Subscription document with marked progress
      try {
        console.log('Attempting to update Subscription document at path:', subscriptionRef.path);
        console.log('Updated levels count:', updatedSubscriptionLevels.length);
        
        await updateDoc(subscriptionRef, {
          levels: updatedSubscriptionLevels
        });
        
        console.log('Successfully updated Subscription document with marked progress');
      } catch (subError) {
        console.error('Error updating Subscription document:', subError);
        toast.error('Failed to persist progress to subscription document');
        throw subError;
      }

      if (updatedCount > 0) {
        const itemTypes = [];
        if (selectedLevels.length > 0) itemTypes.push(`${selectedLevels.length} level(s)`);
        if (selectedSessions.length > 0) itemTypes.push(`${selectedSessions.length} session(s)`);
        if (selectedTopics.length > 0) itemTypes.push(`${selectedTopics.length} topic(s)`);
        if (selectedSubtopics.length > 0) itemTypes.push(`${selectedSubtopics.length} subtopic(s)`);
        
        toast.success(`Successfully marked ${itemTypes.join(', ')} as complete for ${updatedCount} student(s)`);
        if (errorCount > 0) {
          toast.error(`Failed to update ${errorCount} student(s)`);
        }
      } else {
        toast.error('Failed to update any students');
      }

      // Close dialog and reset state
      setMarkProgressDialogOpen(false);
      setSelectedLevelsForProgress({});
      setSelectedSessionsForProgress({});
      setSelectedTopicsForProgress({});
      setSelectedSubtopicsForProgress({});
      setExpandedLevelsForProgress({});
      setExpandedSessionsForProgress({});
      setExpandedTopicsForProgress({});
      
      // Trigger refresh
      setRefreshTrigger(prev => prev + 1);
      
    } catch (error) {
      console.error('Error marking progress:', error);
      toast.error('Failed to mark progress');
    } finally {
      setProgressMarkingLoading(false);
    }
  };

  // Render loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {!importForAll && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
          <Button 
            variant="outlined" 
            startIcon={<LibraryBooksIcon />}
            onClick={() => handleOpenImportDialog(false)}
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              px: 2.5,
              borderWidth: 1.5,
              '&:hover': {
                borderWidth: 1.5,
                backgroundColor: 'primary.50'
              }
            }}
          >
            Import for Current Student
          </Button>
        </Box>
      )}

      {importForAll && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            variant="outlined" 
            startIcon={<CheckCircleIcon />}
            onClick={handleOpenMarkProgressDialog}
            disabled={progressMarkingLoading}
            size="small"
            sx={{ 
              textTransform: 'none',
              borderRadius: 1.5,
              px: 2,
              minWidth: 'auto',
              borderColor: 'success.main',
              color: 'success.main',
              '&:hover': {
                backgroundColor: 'success.50',
                borderColor: 'success.dark'
              }
            }}
          >
            {progressMarkingLoading ? 'Marking...' : 'Mark Progress'}
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<LibraryBooksIcon />}
            onClick={() => handleOpenImportDialog(true)}
            disabled={levelImportLoading}
            size="small"
            sx={{ 
              textTransform: 'none',
              borderRadius: 1.5,
              px: 2,
              minWidth: 'auto',
              '&:hover': {
                backgroundColor: 'primary.50'
              }
            }}
          >
            {levelImportLoading ? 'Importing...' : 'Import'}
          </Button>
        </Box>
      )}

      {!importForAll && selectedSyllabus && (
        <Box sx={{ mt: 1 }}>
          {selectedSyllabus.levels.map((level) => (
            <Card 
              key={level.id} 
              variant="outlined" 
              sx={{ 
                mb: 1.5,
                borderRadius: 2,
                boxShadow: 'none',
                borderWidth: 1.5,
                borderColor: 'divider',
                '&:hover': {
                  borderColor: 'primary.200'
                },
                transition: 'all 0.2s'
              }}
            >
              <CardContent sx={{ p: 0 }}>
                <Accordion 
                  expanded={isLevelExpanded(level.id)}
                  onChange={handleLevelChange(level.id)}
                  disableGutters
                  elevation={0}
                >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ 
                      px: 2,
                      py: 1,
                      minHeight: 48,
                      borderBottom: isLevelExpanded(level.id) ? '1.5px solid' : 'none',
                      borderColor: 'divider',
                      bgcolor: 'background.default',
                      borderRadius: isLevelExpanded(level.id) ? '16px 16px 0 0' : 2,
                      '& .MuiAccordionSummary-content': {
                        m: 0
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Typography variant="h4" sx={{ 
                        fontWeight: 500,
                        fontSize: '0.95rem',
                        color: 'text.primary',
                        letterSpacing: '0.2px',
                        display: 'flex',
                        alignItems: 'center',
                        opacity: 0.9,
                        '&:after': {
                          content: '""',
                          display: 'inline-block',
                          width: 3,
                          height: 3,
                          borderRadius: '50%',
                          backgroundColor: level.status === 'completed' ? 'success.main' : 'grey.400',
                          marginLeft: 1,
                          opacity: 0.8
                        }
                      }}>
                        {level.name}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          bgcolor: 'background.paper',
                          p: 0.5,
                          px: 1,
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'divider'
                        }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={calculateLevelCompletion(level)} 
                            sx={{ 
                              width: 50,
                              height: 3,
                              borderRadius: 2,
                              mr: 1,
                              bgcolor: 'grey.100',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: level.status === 'completed' ? 'success.main' : 'grey.400'
                              }
                            }}
                          />
                          <Typography variant="caption" sx={{ 
                            fontWeight: 500,
                            color: level.status === 'completed' ? 'success.main' : 'text.secondary',
                            fontSize: '0.75rem'
                          }}>
                            {calculateLevelCompletion(level)}%
                          </Typography>
                        </Box>
                        
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteLevel(level.id);
                          }}
                          sx={{ 
                            p: 0.5,
                            color: 'text.secondary',
                            opacity: 0.6,
                            '&:hover': {
                              opacity: 1,
                              bgcolor: 'action.hover'
                            }
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  
                  <AccordionDetails sx={{ p: 1.5, bgcolor: 'background.paper' }}>
                    {level.sessions.map((session) => (
                      <Accordion 
                        key={session.id}
                        expanded={isSessionExpanded(session.id)}
                        onChange={handleSessionChange(session.id)}
                        elevation={0}
                        disableGutters
                        sx={{ 
                          mb: 1,
                          '&:last-child': { mb: 0 },
                          border: '1.5px solid',
                          borderColor: 'divider',
                          '&:before': { display: 'none' },
                          borderRadius: 2,
                          bgcolor: 'background.default',
                          '&:hover': {
                            borderColor: 'primary.100'
                          },
                          transition: 'all 0.2s'
                        }}
                      >
                        <AccordionSummary 
                          expandIcon={<ExpandMoreIcon />}
                          sx={{ 
                            px: 1.5,
                            minHeight: 44,
                            '& .MuiAccordionSummary-content': {
                              m: 0
                            }
                          }}
                        >
                          <Typography sx={{ 
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            color: 'text.primary',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            {session.name}
                          </Typography>
                        </AccordionSummary>
                        
                        <AccordionDetails sx={{ px: 1.5, py: 1 }}>
                          {session.topics.map((topic) => (
                            <Box 
                              key={topic.id} 
                              sx={{ 
                                '&:not(:last-child)': {
                                  mb: 2,
                                  pb: 2,
                                  borderBottom: '1px solid',
                                  borderColor: 'divider'
                                }
                              }}
                            >
                              <Box sx={{ 
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: topic.subtopics?.length > 0 ? 1.5 : 0,
                                pb: 0.5,
                                borderBottom: '2px solid',
                                borderColor: topic.status === 'completed' ? 'success.light' : 'primary.100'
                              }}>
                                <Typography 
                                  sx={{ 
                                    fontSize: '1rem',
                                    fontWeight: 700,
                                    color: topic.status === 'completed' ? 'success.main' : 'text.primary',
                                    transition: 'all 0.2s',
                                    textDecoration: topic.status === 'completed' ? 'line-through' : 'none',
                                    opacity: topic.status === 'completed' ? 0.7 : 1
                                  }}
                                >
                                  {topic.name}
                                </Typography>
                                
                                <Switch
                                  size="small"
                                  checked={topic.status === 'completed'}
                                  onChange={(e) => handleUpdateTopicStatus(
                                    level.id, 
                                    session.id, 
                                    topic.id, 
                                    e.target.checked
                                  )}
                                  color="success"
                                />
                              </Box>
                              
                              {topic.subtopics && topic.subtopics.length > 0 && (
                                <Box sx={{ 
                                  ml: 2,
                                  pl: 1,
                                  borderLeft: '2px solid',
                                  borderColor: 'grey.200'
                                }}>
                                  {topic.subtopics.map((subtopic) => (
                                    <Box 
                                      key={subtopic.id}
                                      sx={{ 
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        py: 0.5,
                                        px: 1,
                                        borderRadius: 1,
                                        transition: 'all 0.15s',
                                        '&:hover': {
                                          bgcolor: 'action.hover'
                                        }
                                      }}
                                    >
                                      <Typography 
                                        sx={{ 
                                          fontSize: '0.875rem',
                                          fontWeight: 400,
                                          color: subtopic.status === 'completed' ? 'text.disabled' : 'text.secondary',
                                          textDecoration: subtopic.status === 'completed' ? 'line-through' : 'none',
                                          transition: 'all 0.2s',
                                          pl: 0.5,
                                          opacity: subtopic.status === 'completed' ? 0.7 : 1
                                        }}
                                      >
                                        • {subtopic.name}
                                      </Typography>
                                      
                                      <Switch
                                        size="small"
                                        checked={subtopic.status === 'completed'}
                                        onChange={(e) => handleUpdateSubtopicStatus(
                                          level.id,
                                          session.id,
                                          topic.id,
                                          subtopic.id,
                                          e.target.checked
                                        )}
                                        color="success"
                                      />
                                    </Box>
                                  ))}
                                </Box>
                              )}
                            </Box>
                          ))}
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </AccordionDetails>
                </Accordion>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Import Levels Dialog */}
      <Dialog 
        open={importLevelsDialogOpen} 
        onClose={() => {
          setImportLevelsDialogOpen(false);
          setSelectedMasterSyllabus(null);
          setSelectedLevelsToImport({});
        }}
        maxWidth="md"
        fullWidth
      > 
        <DialogTitle>
          {importForAllUsers ? 'Import Levels for All Students' : 'Import Levels for Current Student'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            {!selectedMasterSyllabus ? (
              <>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Select a master syllabus to import levels from:
                </Typography>
                
                {masterSyllabi.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {masterSyllabi.map((syllabus) => (
                      <Box 
                        key={syllabus.id} 
                        sx={{ 
                          p: 2, 
                          border: '1px solid #ddd',
                          borderRadius: 1,
                          cursor: 'pointer',
                          '&:hover': { 
                            borderColor: 'primary.main',
                            backgroundColor: 'rgba(25, 118, 210, 0.04)'
                          }
                        }}
                        onClick={() => handleSelectMasterSyllabus(syllabus)}
                      >
                        <Typography variant="h6">{syllabus.name}</Typography>
                        <Typography variant="caption" display="block">
                          Last modified: {new Date(syllabus.lastModified).toLocaleString()}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {syllabus.levels.length} level(s) available
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body1">
                    No master syllabi available. Please create a master syllabus first.
                  </Typography>
                )}
              </>
            ) : (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1">
                    Select levels from "{selectedMasterSyllabus.name}" to import:
                  </Typography>
                  <Button 
                    variant="text" 
                    onClick={() => setSelectedMasterSyllabus(null)}
                  >
                    Back to Syllabi List
                  </Button>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {selectedMasterSyllabus.levels.map((level) => (
                    <FormControlLabel
                      key={level.id}
                      control={
                        <Checkbox 
                          checked={selectedLevelsToImport[level.id] || false}
                          onChange={() => handleToggleLevelSelection(level.id)}
                        />
                      }
                      label={
                        <Box sx={{ ml: 1 }}>
                          <Typography variant="subtitle2">{level.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {level.sessions.length} session(s), 
                            {level.sessions.reduce((sum, session) => sum + (session.topics?.length || 0), 0)} topic(s)
                          </Typography>
                        </Box>
                      }
                      sx={{ 
                        p: 1, 
                        border: '1px solid #eee',
                        borderRadius: 1,
                        m: 0
                      }}
                    />
                  ))}
                </Box>
                
                {selectedMasterSyllabus.levels.length === 0 && (
                  <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
                    This syllabus has no levels to import.
                  </Typography>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setImportLevelsDialogOpen(false);
            setSelectedMasterSyllabus(null);
            setSelectedLevelsToImport({});
          }}>
            Cancel
          </Button>
          {selectedMasterSyllabus && (
            <Button 
              variant="contained" 
              onClick={handleImportLevels}
              disabled={
                levelImportLoading || 
                !Object.values(selectedLevelsToImport).some(selected => selected)
              }
              startIcon={levelImportLoading && <CircularProgress size={20} color="inherit" />}
            >
              {levelImportLoading ? 'Importing...' : `Import Selected Levels${importForAllUsers ? ' for All Students' : ''}`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Mark Progress Dialog */}
      <Dialog 
        open={markProgressDialogOpen} 
        onClose={() => {
          setMarkProgressDialogOpen(false);
          setSelectedLevelsForProgress({});
          setSelectedSessionsForProgress({});
          setSelectedTopicsForProgress({});
          setSelectedSubtopicsForProgress({});
          setExpandedLevelsForProgress({});
          setExpandedSessionsForProgress({});
          setExpandedTopicsForProgress({});
        }}
        maxWidth="lg"
        fullWidth
      > 
        <DialogTitle>
          Mark Progress for Selected Items
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Select items to mark as complete for all students in this batch:
            </Typography>
            
            <Box sx={{ maxHeight: 500, overflowY: 'auto' }}>
              {getAssignedSyllabusTree().map((level) => (
                <Box key={level.levelId} sx={{ mb: 2 }}>
                  {/* Level */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    p: 1, 
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    bgcolor: '#fafafa'
                  }}>
                    <FormControlLabel
                      control={
                        <Checkbox 
                          checked={selectedLevelsForProgress[level.levelId] || false}
                          onChange={() => handleToggleLevelProgressSelection(level.levelId)}
                          color="success"
                        />
                      }
                      label={
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                          {level.levelName}
                        </Typography>
                      }
                      sx={{ flex: 1, m: 0 }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleToggleLevelExpansion(level.levelId)}
                      sx={{ ml: 1 }}
                    >
                      <ExpandMore 
                        sx={{ 
                          transform: expandedLevelsForProgress[level.levelId] ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s'
                        }} 
                      />
                    </IconButton>
                  </Box>

                  {/* Sessions */}
                  {expandedLevelsForProgress[level.levelId] && (
                    <Box sx={{ ml: 3, mt: 1 }}>
                      {level.sessions.map((session) => (
                        <Box key={session.sessionId} sx={{ mb: 1 }}>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            p: 1, 
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            bgcolor: '#f5f5f5'
                          }}>
                            <FormControlLabel
                              control={
                                <Checkbox 
                                  checked={selectedSessionsForProgress[session.sessionId] || false}
                                  onChange={() => handleToggleSessionProgressSelection(session.sessionId)}
                                  color="success"
                                />
                              }
                              label={
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  {session.sessionName}
                                </Typography>
                              }
                              sx={{ flex: 1, m: 0 }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => handleToggleSessionExpansion(session.sessionId)}
                              sx={{ ml: 1 }}
                            >
                              <ExpandMore 
                                sx={{ 
                                  transform: expandedSessionsForProgress[session.sessionId] ? 'rotate(180deg)' : 'rotate(0deg)',
                                  transition: 'transform 0.2s'
                                }} 
                              />
                            </IconButton>
                          </Box>

                          {/* Topics */}
                          {expandedSessionsForProgress[session.sessionId] && (
                            <Box sx={{ ml: 3, mt: 1 }}>
                              {session.topics.map((topic) => (
                                <Box key={topic.topicId} sx={{ mb: 1 }}>
                                  <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    p: 1, 
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 1,
                                    bgcolor: '#fafafa'
                                  }}>
                                    <FormControlLabel
                                      control={
                                        <Checkbox 
                                          checked={selectedTopicsForProgress[topic.topicId] || false}
                                          onChange={() => handleToggleTopicProgressSelection(topic.topicId)}
                                          color="success"
                                        />
                                      }
                                      label={
                                        <Typography variant="body2">
                                          {topic.topicName}
                                        </Typography>
                                      }
                                      sx={{ flex: 1, m: 0 }}
                                    />
                                    <IconButton
                                      size="small"
                                      onClick={() => handleToggleTopicExpansion(topic.topicId)}
                                      sx={{ ml: 1 }}
                                    >
                                      <ExpandMore 
                                        sx={{ 
                                          transform: expandedTopicsForProgress[topic.topicId] ? 'rotate(180deg)' : 'rotate(0deg)',
                                          transition: 'transform 0.2s'
                                        }} 
                                      />
                                    </IconButton>
                                  </Box>

                                  {/* Subtopics */}
                                  {expandedTopicsForProgress[topic.topicId] && (
                                    <Box sx={{ ml: 3, mt: 1 }}>
                                      {topic.subtopics.map((subtopic) => (
                                        <Box key={subtopic.subtopicId} sx={{ mb: 1 }}>
                                          <Box sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            p: 1, 
                                            border: '1px solid #e0e0e0',
                                            borderRadius: 1,
                                            bgcolor: '#f5f5f5'
                                          }}>
                                            <FormControlLabel
                                              control={
                                                <Checkbox 
                                                  checked={selectedSubtopicsForProgress[subtopic.subtopicId] || false}
                                                  onChange={() => handleToggleSubtopicProgressSelection(subtopic.subtopicId)}
                                                  color="success"
                                                />
                                              }
                                              label={
                                                <Typography variant="body2">
                                                  {subtopic.subtopicName}
                                                </Typography>
                                              }
                                              sx={{ flex: 1, m: 0 }}
                                            />
                                          </Box>
                                        </Box>
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
            
            {/* Empty state */}
            {getAssignedSyllabusTree().length === 0 && (
              <Box sx={{ mt: 2, textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No syllabus items available to mark progress.
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Only items that have been imported to this subscription are shown.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setMarkProgressDialogOpen(false);
            setSelectedLevelsForProgress({});
            setSelectedSessionsForProgress({});
            setSelectedTopicsForProgress({});
            setSelectedSubtopicsForProgress({});
            setExpandedLevelsForProgress({});
            setExpandedSessionsForProgress({});
            setExpandedTopicsForProgress({});
          }}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleMarkProgress}
            disabled={
              progressMarkingLoading || 
              !(
                Object.values(selectedLevelsForProgress).some(selected => selected) ||
                Object.values(selectedSessionsForProgress).some(selected => selected) ||
                Object.values(selectedTopicsForProgress).some(selected => selected) ||
                Object.values(selectedSubtopicsForProgress).some(selected => selected)
              )
            }
            startIcon={progressMarkingLoading && <CircularProgress size={20} color="inherit" />}
            sx={{
              bgcolor: 'success.main',
              '&:hover': {
                bgcolor: 'success.dark'
              }
            }}
          >
            {progressMarkingLoading ? 'Marking Progress...' : 'Mark Selected Items'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SyllabusSection; 